import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { CHALLENGES } from '../../data/challenges';

/**
 * Homepage section #2 per IA spec — "Parenting Challenges".
 *
 * Five large challenge cards that let visitors self-identify by problem
 * rather than category. Each card links to /challenges/[slug].
 */
const ParentingChallengesSection = ({ content }) => {
  const eyebrow = content?.eyebrow || 'Browse by challenge';
  const heading = content?.heading || 'What are you navigating right now?';
  const intro = content?.intro || 'Pick what feels most pressing today. We\u2019ll show you the toolkits built for exactly that.';
  const seeAllText = content?.see_all_text || 'See all parenting challenges';

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="parenting-challenges-section">
      <div className="wrap">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <p className="text-sm font-semibold text-[#F97316] uppercase tracking-wider mb-4">
            {eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
            {heading}
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            {intro}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {CHALLENGES.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.slug}
                to={`/challenges/${c.slug}`}
                className="group bg-white rounded-2xl p-6 md:p-7 border border-slate-200 hover:border-teal-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                data-testid={`home-challenge-${c.slug}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#FEF9EF] flex items-center justify-center mb-5 group-hover:bg-[#FDEDD8] transition-colors">
                  <Icon className="h-6 w-6 text-[#F97316]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{c.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-4">{c.short}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 group-hover:gap-2 transition-all">
                  See toolkits <ChevronRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/challenges"
            className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:gap-2 transition-all"
            data-testid="home-all-challenges-link"
          >
            {seeAllText} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ParentingChallengesSection;
